import { useMemo, useState } from 'react';
import { Alert, Card, Space, Typography } from 'antd';
import {
  ErpDataGrid,
  ErpDataGridStatusTag,
  useErpMaster,
  formatINR
} from '../../components/common/erp-grid';
import ErpPivotView from '../../components/common/erp-grid/ErpPivotView';

// ─────────────────────────────────────────────────────────────────
// Glass-i — /dev/erp-grid-bi-demo (Stage 6A Grid Increment 3)
//
// Demonstrates the THREE analytical surfaces shipped in Increment 3:
//   1. Nested AND / OR advanced filter modal  (Filters button on the
//      grid toolbar opens the nested rule builder).
//   2. Grouping with per-group aggregates     (Group dropdown on the
//      toolbar; pick "State" or "Status" to see colspan headers with
//      record count + sum-of-credit-limit per group).
//   3. Lightweight pivot preview              (ErpPivotView below
//      the grid; pick rows / columns / value / aggregation).
//
// Everything is LOCAL STATE — no API calls, no DB writes, no real
// workflow impact. Safe to remove by deleting:
//   - this page file
//   - the `/dev/erp-grid-bi-demo` route entry in routes/index.jsx
// ─────────────────────────────────────────────────────────────────

const STATES = [
  { code: '27', label: 'Maharashtra' },
  { code: '29', label: 'Karnataka' },
  { code: '33', label: 'Tamil Nadu' },
  { code: '07', label: 'Delhi' },
  { code: '24', label: 'Gujarat' },
  { code: '19', label: 'West Bengal' }
];

const STATUS_OPTIONS = [
  { label: 'Active',   value: 'active'   },
  { label: 'Inactive', value: 'inactive' },
  { label: 'Blocked',  value: 'blocked'  }
];

const CATEGORIES = ['Retail', 'Wholesale', 'Project', 'Export'];

const COMPANIES = [
  'Sri Balaji Toughened Glass',
  'Chennai Safety Glass',
  'Kovai Glass Works',
  'Madurai Laminates',
  'Bengaluru Glass Solutions',
  'Apex Facade Systems',
  'Greenline DGU Glass',
  'Crystal Edge Processors',
  'Tamilnadu Toughened Glass',
  'Coastal Mirror Co',
  'Vista Glass House',
  'Royal Glass Mfg',
  'NorthStar Glazing',
  'Mountain Safety Glass',
  'Pinnacle Glassworks',
  'Skyline Glazing',
  'Heritage Glass Traders',
  'Granite Glass Pvt Ltd',
  'Sundeck Glass Studio',
  'Saraswati Mirror & Glass',
  'Western Ghats Glazing',
  'Hyderabad Insulated Glass',
  'Coromandel Float Glass Co',
  'Konkan Toughened Glass',
  'Deccan Mirror Industries'
];

// Status distribution — Active (~70%) / Inactive (~20%) / Blocked (~10%).
function statusForIndex(i) {
  if (i === 5 || i === 19 || i === 23) return 'blocked';
  if (i === 3 || i === 9 || i === 14 || i === 20) return 'inactive';
  return 'active';
}

function buildSampleData() {
  const rows = [];
  COMPANIES.forEach((name, i) => {
    const state = STATES[i % STATES.length];
    const status = statusForIndex(i);
    const category = CATEGORIES[i % CATEGORIES.length];
    rows.push({
      id: `cust-${String(i + 1).padStart(3, '0')}`,
      code: `CUST-${String(2000 + i + 1)}`,
      name,
      stateCode: state.code,
      stateName: state.label,
      category,
      status,
      // Vary credit + invoiced so aggregates differ between sum / avg.
      creditLimit: ((i % 7) + 1) * 100_000,
      ytdInvoiced: ((i % 9) + 1) * 75_000 + (i % 4) * 12_500,
      openOrders: (i % 5) + 1
    });
  });
  return rows;
}

function buildColumns() {
  return [
    {
      title: 'Customer',
      children: [
        {
          title: 'Code',
          dataIndex: 'code',
          key: 'code',
          width: 120,
          fixed: 'left',
          filterable: true
        },
        {
          title: 'Name',
          dataIndex: 'name',
          key: 'name',
          width: 260,
          ellipsis: true,
          filterable: true,
          dataType: 'string'
        }
      ]
    },
    {
      title: 'Segmentation',
      children: [
        {
          title: 'State',
          dataIndex: 'stateName',
          key: 'stateName',
          width: 160,
          // `groupable: true` exposes the column in the Group-by
          // dropdown AND in the ErpPivotView axis selectors.
          groupable: true,
          pivotable: true,
          filterable: true,
          dataType: 'enum',
          filters: STATES.map((s) => ({ text: s.label, value: s.label }))
        },
        {
          title: 'Status',
          dataIndex: 'status',
          key: 'status',
          width: 130,
          groupable: true,
          pivotable: true,
          filterable: true,
          dataType: 'enum',
          filters: STATUS_OPTIONS.map((o) => ({ text: o.label, value: o.value })),
          render: (v) => <ErpDataGridStatusTag status={v} />
        },
        {
          title: 'Category',
          dataIndex: 'category',
          key: 'category',
          width: 140,
          groupable: true,
          pivotable: true,
          filterable: true,
          dataType: 'enum',
          filters: CATEGORIES.map((c) => ({ text: c, value: c }))
        }
      ]
    },
    {
      title: 'Commercial',
      children: [
        {
          title: 'Credit Limit',
          dataIndex: 'creditLimit',
          key: 'creditLimit',
          width: 160,
          align: 'right',
          dataType: 'number',
          // `aggregate: 'sum'` + `aggregatable: true` opts this column
          // into the summary footer AND the pivot value selector.
          aggregate: 'sum',
          aggregatable: true,
          filterable: true,
          render: (v) => formatINR(v)
        },
        {
          title: 'YTD Invoiced',
          dataIndex: 'ytdInvoiced',
          key: 'ytdInvoiced',
          width: 160,
          align: 'right',
          dataType: 'number',
          aggregate: 'sum',
          aggregatable: true,
          filterable: true,
          render: (v) => formatINR(v)
        },
        {
          title: 'Open Orders',
          dataIndex: 'openOrders',
          key: 'openOrders',
          width: 130,
          align: 'right',
          dataType: 'number',
          aggregate: 'sum',
          aggregatable: true,
          filterable: true
        }
      ]
    }
  ];
}

export default function ErpGridBiDemoPage() {
  // Reuse the standard client-mode controller. We only need rows +
  // refresh; no CRUD is involved in this demo.
  const { rows, gridHandlers } = useErpMaster({
    gridId: 'demo-grid-bi',
    entityLabel: 'Customer',
    resourceType: 'customer',
    initialRows: useMemo(() => buildSampleData(), [])
  });

  const columns = useMemo(() => buildColumns(), []);

  // Local state for the pivot config so users can clearly see this
  // section is independent of the grid above.
  const [pivotKey] = useState('demo-pivot-bi');

  return (
    <div className="space-y-4">
      <div>
        <Typography.Title level={4} className="!mb-1">
          Grid Increment 3 — Nested filters + group summaries + pivot preview
        </Typography.Title>
        <Typography.Paragraph type="secondary" className="!mb-0">
          Sandbox preview for Stage 6A Grid Increment 3. Local data only —
          no API calls, no DB writes. Safe to delete.
        </Typography.Paragraph>
      </div>

      <Alert
        type="info"
        showIcon
        message="What to try"
        description={
          <ul className="!my-0 !pl-4 list-disc">
            <li>
              Click <b>Filters</b> on the toolbar to open the nested
              AND/OR rule builder. Use <b>Add group</b> to nest conditions
              like <code>(Category = Wholesale AND (State = Maharashtra OR Karnataka))</code>.
            </li>
            <li>
              Use <b>Group</b> on the toolbar to group by State, Status,
              or Category. Group headers show per-group <i>count</i> + sum
              of <i>Credit Limit / YTD Invoiced</i> from the current page.
            </li>
            <li>
              Below the grid, the <b>Pivot preview</b> aggregates the same
              dataset — rows × columns × measure with sum/avg/min/max/count.
            </li>
          </ul>
        }
      />

      <Card bordered size="small" className="erp-grid__card">
        <ErpDataGrid
          title="Customer master (BI demo)"
          gridId="demo-grid-bi"
          columns={columns}
          {...gridHandlers}
          rowKey="id"
          actions={{ view: false, edit: false, delete: false }}
          exportable
        />
      </Card>

      <ErpPivotView
        gridId={pivotKey}
        columns={columns}
        rows={rows}
        title="Pivot preview — operational customer dataset"
        description="Aggregates the current in-memory list. Switch to server-side aggregation for production reporting dashboards."
      />

      <Alert
        type="warning"
        showIcon
        message="Pivot performance reminder"
        description="This pivot runs in your browser over the current dataset only. For analytics over millions of rows, use SQL GROUP BY, materialised views, or a dedicated reporting schema — never load raw rows into the pivot for that."
      />
    </div>
  );
}
